/**
 * Editor for a Progress Note addendum. The note itself is never loaded here —
 * an addendum is a separate document, so the original stays read-only by
 * construction.
 *
 * "Sign & Add" confirms before writing anything, then saves and signs: the
 * sign endpoint acts on a stored addendum, and the confirmation belongs before
 * the record becomes permanent, not between the two calls.
 */
import { useEffect, useState } from 'react'
import { Button, Group, Modal, Select, Stack, Text, Textarea } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconAlertTriangle } from '@tabler/icons-react'
import {
  ADDENDUM_REASON_LABELS,
  useSaveAddendum,
  useSignAddendum,
  type AddendumReason,
  type NoteAddendum,
} from '@/pages/Patient/hooks/useNoteAddenda'

const REASON_OPTIONS = (Object.keys(ADDENDUM_REASON_LABELS) as AddendumReason[]).map((value) => ({
  value,
  label: ADDENDUM_REASON_LABELS[value],
}))

export type AddendumEditorModalProps = {
  opened: boolean
  onClose: () => void
  documentReferenceId: string
  draft?: NoteAddendum
  onChanged: () => void
}

export function AddendumEditorModal({
  opened,
  onClose,
  documentReferenceId,
  draft,
  onChanged,
}: AddendumEditorModalProps) {
  const [content, setContent] = useState('')
  const [reason, setReason] = useState<AddendumReason | null>(null)
  const [action, setAction] = useState<'draft' | 'sign' | null>(null)

  const save = useSaveAddendum(onChanged)
  const sign = useSignAddendum(onChanged)

  useEffect(() => {
    if (!opened) return
    setContent(draft?.content ?? '')
    setReason(draft?.reason ?? null)
  }, [opened, draft])

  const canSubmit = content.trim().length > 0 && !!reason
  const busy = action !== null

  // Signing saves first, so both paths share one submit. The empty catch is
  // deliberate: each mutation reports its own failure through a notification,
  // and this only stops the rejection escaping as an unhandled promise.
  const submit = async (mode: 'draft' | 'sign') => {
    if (!canSubmit) return
    setAction(mode)
    try {
      const saved = await save.mutateAsync({
        documentReferenceId,
        addendumId: draft?.id,
        content: content.trim(),
        reason,
      })
      if (mode === 'sign') {
        await sign.mutateAsync({ addendumId: saved.addendumId })
      }
      onClose()
    } catch {
      // Already surfaced by the mutation.
    } finally {
      setAction(null)
    }
  }

  const confirmThenSign = () => {
    if (!canSubmit) return
    modals.openConfirmModal({
      title: 'Sign this addendum?',
      children: (
        <Text size="sm" c="dark.7" style={{ lineHeight: 1.65 }}>
          A signed addendum becomes a <strong>permanent part of the medical record</strong>. It
          cannot be edited or deleted, and it does not replace the original note. Corrections
          require a further addendum.
        </Text>
      ),
      labels: { confirm: 'Sign & Add', cancel: 'Cancel' },
      confirmProps: { color: '#1e3a5f' },
      onConfirm: () => void submit('sign'),
    })
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Add Addendum" size="lg" centered>
      <Stack gap="md">
        <Select
          label="Reason"
          placeholder="Select a reason"
          data={REASON_OPTIONS}
          value={reason}
          onChange={(value) => setReason(value as AddendumReason | null)}
          allowDeselect={false}
          withAsterisk
          data-testid="addendum-reason"
        />
        <Textarea
          label="Addendum"
          placeholder="Correction, clarification, or additional information"
          autosize
          minRows={5}
          value={content}
          onChange={(event) => setContent(event.currentTarget.value)}
          withAsterisk
          data-testid="addendum-content"
        />
        <Group
          gap="xs"
          align="flex-start"
          wrap="nowrap"
          className="rounded-lg border border-[#fde68a] bg-[#fffbeb] px-[14px] py-[10px]"
          data-testid="addendum-permanence-notice"
        >
          <IconAlertTriangle size={14} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
          <Text size="sm" c="#b45309" style={{ lineHeight: 1.55 }}>
            Signing adds this to the permanent record. The original note is never changed.
          </Text>
        </Group>
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => void submit('draft')}
            disabled={!canSubmit || busy}
            loading={action === 'draft'}
            data-testid="addendum-save-draft"
          >
            Save Draft
          </Button>
          <Button
            onClick={confirmThenSign}
            disabled={!canSubmit || busy}
            loading={action === 'sign'}
            color="#1e3a5f"
            data-testid="addendum-sign"
          >
            Sign &amp; Add
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
